import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import type { TradingAccount } from '../types'
import { useAppPath } from '../nav'
import { moneyAmount } from '../safe-ui'
import { classifyLiveCreateFailure, type LiveCreateFailCopy } from '../live-create-errors'

type Phase = 'password' | 'progress' | 'success' | 'error'

type Props = {
  open: boolean
  onClose: () => void
  onCreate: (tradingPassword: string, confirmTradingPassword: string) => Promise<TradingAccount>
  onSucceeded?: (account: TradingAccount) => void
  onFailed?: (failure: LiveCreateFailCopy) => void
}

/** Soft status lines while a single backend create is in flight — never claim a step passed. */
const STATUS_LINES = [
  'Connecting securely…',
  'Preparing MetaTrader account…',
  'Verifying account…',
  'Finalizing setup…',
] as const

const TRADING_PASSWORD_SYMBOLS = '!@#$%^&*-_=+'

function isStrongEnough(password: string): boolean {
  if (password.length < 8 || password.length > 32) return false
  if (!/[A-Z]/.test(password)) return false
  if (!/[a-z]/.test(password)) return false
  if (!/\d/.test(password)) return false
  if (![...TRADING_PASSWORD_SYMBOLS].some((c) => password.includes(c))) return false
  return true
}

export function LiveCreateModal({ open, onClose, onCreate, onSucceeded, onFailed }: Props) {
  const appPath = useAppPath()
  const navigate = useNavigate()
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const [phase, setPhase] = useState<Phase>('password')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [localError, setLocalError] = useState('')
  const [failure, setFailure] = useState<LiveCreateFailCopy | null>(null)
  const [statusLine, setStatusLine] = useState(0)
  const [created, setCreated] = useState<TradingAccount | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const busy = phase === 'progress' || submitting

  useEffect(() => {
    if (!open) {
      setPhase('password')
      setPassword('')
      setConfirm('')
      setLocalError('')
      setFailure(null)
      setStatusLine(0)
      setCreated(null)
      setSubmitting(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => {
      const focusable = panelRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      focusable?.focus()
    }, 40)
    return () => window.clearTimeout(t)
  }, [open, phase])

  useEffect(() => {
    if (phase !== 'progress') return
    const id = window.setInterval(() => {
      setStatusLine((prev) => (prev + 1) % STATUS_LINES.length)
    }, 2400)
    return () => window.clearInterval(id)
  }, [phase])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (busy) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, busy, onClose])

  if (!open) return null

  function closeIfAllowed() {
    if (busy) return
    onClose()
  }

  function validate(): string {
    if (password.length < 8 || password.length > 32) {
      return 'Trading password must be 8–32 characters.'
    }
    if (!/[A-Z]/.test(password)) {
      return 'Include at least one uppercase letter (A–Z).'
    }
    if (!/[a-z]/.test(password)) {
      return 'Include at least one lowercase letter (a–z).'
    }
    if (!/\d/.test(password)) {
      return 'Include at least one digit (0–9).'
    }
    if (![...TRADING_PASSWORD_SYMBOLS].some((c) => password.includes(c))) {
      return `Include a symbol from ${TRADING_PASSWORD_SYMBOLS}`
    }
    if (!isStrongEnough(password)) {
      return 'Password does not meet MT5 trading password rules.'
    }
    if (password !== confirm) {
      return 'Passwords do not match.'
    }
    return ''
  }

  async function runCreate() {
    const validation = validate()
    if (validation) {
      setLocalError(validation)
      setPhase('password')
      return
    }

    setLocalError('')
    setFailure(null)
    setSubmitting(true)
    setPhase('progress')
    setStatusLine(0)

    try {
      const account = await onCreate(password, confirm)
      setCreated(account)
      setPhase('success')
      onSucceeded?.(account)
    } catch (err) {
      const classified = classifyLiveCreateFailure(err)
      if (classified.kind === 'validation') {
        setLocalError(classified.text)
        setPhase('password')
      } else {
        setFailure(classified)
        setPhase('error')
        onFailed?.(classified)
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    await runCreate()
  }

  async function retry() {
    if (busy) return
    await runCreate()
  }

  return (
    <div
      className={`cp-modal cp-live-flow${phase !== 'password' ? ' cp-live-flow--full' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-busy={busy}
    >
      <button
        type="button"
        className="cp-modal__backdrop"
        aria-label="Close"
        onClick={closeIfAllowed}
        disabled={busy}
        tabIndex={busy ? -1 : 0}
      />
      <div className="cp-modal__panel cp-live-flow__panel" ref={panelRef}>
        {phase === 'password' ? (
          <>
            <div className="cp-modal__head">
              <div>
                <h2 id={titleId}>Choose MT5 Password</h2>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={closeIfAllowed} disabled={busy}>
                Close
              </Button>
            </div>
            <p className="cp-muted">
              Create your Live MetaTrader 5 account. Opening balance is 0 USD — fund with a real
              deposit after creation.
            </p>
            <form className="cp-live-flow__form" onSubmit={(e) => void submit(e)}>
              <label className="cp-field">
                MT5 Trading Password
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={32}
                  required
                  disabled={busy}
                />
              </label>
              <label className="cp-field">
                Confirm Password
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={32}
                  required
                  disabled={busy}
                />
              </label>
              <p className="cp-note">
                Use 8–32 characters with uppercase, lowercase, a digit, and a symbol (
                {TRADING_PASSWORD_SYMBOLS}). Perfect Predict never displays MT5 passwords after
                creation.
              </p>
              {localError ? (
                <p className="cp-live-flow__warn" role="alert">
                  <span aria-hidden="true">⚠</span>
                  <span>{localError}</span>
                </p>
              ) : null}
              <div className="cp-live-flow__actions">
                <Button type="submit" variant="primary" disabled={busy}>
                  {busy ? (
                    <span className="cp-live-flow__btn-busy">
                      <span className="cp-live-flow__spinner" aria-hidden="true" />
                      Creating Live Account…
                    </span>
                  ) : (
                    'Create Live Account'
                  )}
                </Button>
                <Button type="button" variant="secondary" onClick={closeIfAllowed} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </form>
          </>
        ) : null}

        {phase === 'progress' ? (
          <div className="cp-live-flow__progress" role="status" aria-live="polite">
            <div className="cp-live-flow__spinner cp-live-flow__spinner--lg" aria-hidden="true" />
            <h2 id={titleId}>Creating your Live Trading Account</h2>
            <p className="cp-muted cp-live-flow__status-line">{STATUS_LINES[statusLine]}</p>
            <p className="cp-note">Please wait — do not close this window.</p>
          </div>
        ) : null}

        {phase === 'success' && created ? (
          <div className="cp-live-flow__success">
            <span className="cp-badge cp-badge--live">Active</span>
            <h2 id={titleId}>Live Trading Account Ready</h2>
            <p className="cp-muted">
              Your Live MetaTrader 5 account has been created successfully.
            </p>
            <dl className="cp-live-flow__creds">
              <div>
                <dt>MT5 Login</dt>
                <dd className="cp-num">{created.mt5Login}</dd>
              </div>
              <div>
                <dt>Trading Server</dt>
                <dd>iBull Capital - Live</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{created.status || 'Active'}</dd>
              </div>
              <div>
                <dt>Opening Balance</dt>
                <dd className="cp-num">{moneyAmount(created.balance)} USD</dd>
              </div>
            </dl>
            <div className="cp-live-flow__actions">
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  onClose()
                  navigate(appPath('/app/deposits'))
                }}
              >
                Deposit Funds
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  onClose()
                  navigate(appPath('/app/trade'))
                }}
              >
                Trade Now
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  onClose()
                  navigate(appPath('/app/accounts'))
                }}
              >
                Trading Accounts
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : null}

        {phase === 'error' && failure ? (
          <div className="cp-live-flow__error">
            <div className="cp-live-flow__warn" role="alert">
              <span aria-hidden="true">⚠</span>
              <div>
                <strong id={titleId}>{failure.title}</strong>
                <p>{failure.text}</p>
              </div>
            </div>
            <div className="cp-live-flow__actions">
              {failure.kind === 'already_exists' ? (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      onClose()
                      navigate(appPath('/app/accounts'))
                    }}
                  >
                    Go to Trading Accounts
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      onClose()
                      navigate(appPath('/app/trade'))
                    }}
                  >
                    Trade Now
                  </Button>
                  <Button type="button" variant="ghost" onClick={onClose}>
                    Close
                  </Button>
                </>
              ) : failure.kind === 'configuration' ? (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      onClose()
                      navigate(appPath('/app/support'))
                    }}
                  >
                    Contact Support
                  </Button>
                  <Button type="button" variant="secondary" onClick={onClose}>
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Button type="button" variant="primary" onClick={() => void retry()} disabled={busy}>
                    {busy ? 'Creating Live Account…' : 'Retry'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      onClose()
                      navigate(appPath('/app/support'))
                    }}
                    disabled={busy}
                  >
                    Contact Support
                  </Button>
                  <Button type="button" variant="ghost" onClick={closeIfAllowed} disabled={busy}>
                    Close
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
